export class MixEncodedChunk {
    CHUNK_TYPE = ['audio', 'video', 'audio_meta']
    constructor(type, chunk) {
        this.timestamp = Date.now() // ! Dont use this for audio/video track syncing
        if (this.CHUNK_TYPE.includes(type)) {
            this.type = type
            this.chunk = chunk
        } else {
            throw Error(`Invalid chunk type: ${type}`)
        }
    }
}

export class WebCodecRecorder {
    static muxer = undefined
    codec = 'avc1.42E01E'

    constructor(mediaStream) {
        if (!(mediaStream instanceof MediaStream))
            throw Error(
                `Expecting input of type MediaStream, but getting ${typeof mediaStream} instead.`
            )

        this.mediaStream = mediaStream
        this.state = 'inactive'
        this.ondataavailable = undefined
        this.onstop = () => {}
        this.video_frame_cnt = 0
        this.key_frame_freq = 60
        this.last_frame_time = null
        this.record_start_time = null

        this.videoFrameReader = undefined
        this.audioFrameReader = undefined
        this.videoEncoder = undefined
        this.audioEncoder = undefined
        this.videoEncoderPaused = false
        this.audioEncoderPaused = false
        this.videoSetting = undefined
        this.audioSetting = undefined
        this.video_descriptor = undefined
        this.audio_descriptor = undefined

        this.hasAudio = this.mediaStream.getAudioTracks().length > 0
        if (this.hasAudio) this.build_audio_encoder()
        this.build_video_encoder()
    }

    build_audio_encoder() {
        let aud_track = this.mediaStream.getAudioTracks()[0]
        let audio_settings = aud_track.getSettings()
        console.log('[wbcRecorder] audio setting: ', audio_settings)

        const init = {
            output: (chunk, meta) => {
                // console.dir(chunk);
                // console.log('[wbcRecorder] audio encoded', chunk, meta)

                if (meta.decoderConfig) {
                    if (meta.decoderConfig.description) {
                        console.warn('[wbcRecorder] Find decoderConfig')
                        this.audio_descriptor = new Uint8Array(
                            meta.decoderConfig.description
                        )
                    }
                }
                if (this.ondataavailable !== undefined) {
                    this.ondataavailable(new MixEncodedChunk('audio', chunk))
                }
            },
            error: (e) => {
                console.error(e)
                this.stop()
            },
        }

        const config = {
            codec: 'opus',
            sampleRate: audio_settings.sampleRate,
            numberOfChannels: audio_settings.channelCount,
            // bitrate: 10e6,
        }

        this.audioEncoder = new window.AudioEncoder(init)
        this.audioEncoder.configure(config)
        this.audioSetting = audio_settings
    }

    build_video_encoder() {
        let vid_track = this.mediaStream.getVideoTracks()[0]
        let video_settings = vid_track.getSettings()
        if (
            video_settings.height === undefined ||
            video_settings.width === undefined
        )
            throw Error('Video resolution is not defined! :(')

        const init = {
            output: (chunk, meta) => {
                if (meta.decoderConfig) {
                    if (meta.decoderConfig.description) {
                        console.warn('[wbcRecorder] Find decoderConfig')
                        this.video_descriptor = new Uint8Array(
                            meta.decoderConfig.description
                        )
                    }
                }
                if (this.ondataavailable !== undefined) {
                    this.ondataavailable(new MixEncodedChunk('video', chunk))
                }
            },
            error: (e) => {
                console.error(e)
                this.stop()
            },
        }
        const pixel_multiplier = Math.pow(
            (video_settings.width * video_settings.height) / (320 * 240),
            0.5
        )
        const config = {
            codec: this.codec,
            width: video_settings.width,
            height: video_settings.height,
            bitrate: Math.max(pixel_multiplier, 1) * 250e3,
            framerate: video_settings.frameRate,
            avc: {
                format: 'annexb',
            },
        }

        this.videoEncoder = new window.VideoEncoder(init)
        this.videoEncoder.configure(config)
        this.videoSetting = video_settings
    }

    start() {
        if (this.state != 'inactive')
            throw Error('Recorder is already started!')
        this.state = 'recording' // ! MUST update the state before frame reader is create
        window.logManager.log(
            '%c [wbcRecorder] start',
            'color: rgb(0, 200, 0);'
        )

        let vid_track = this.mediaStream.getVideoTracks()[0]
        let vprc = new window.MediaStreamTrackProcessor(vid_track)
        this.videoFrameReader = vprc.readable.getReader()
        this.videoEncoderPaused = false

        if (this.videoEncoder.state == 'closed') {
            // encoder may closed by browser due to inactivity
            this.build_video_encoder()
        }

        const processVideoFrame = ({ done, value }) => {
            if (this.state == 'inactive' || done) {
                window.logManager.warn('[wbcRecorder] CLOSE video encoder')
                this.videoEncoderPaused = true
                value.close()
                // Wait for audio encoder if there is one
                if (this.hasAudio) {
                    if (this.audioEcoderPaused) {
                        this.onstop()
                        window.logManager.warn(
                            '[wbcRecorder] onstop callback from video encoder'
                        )
                    }
                } else {
                    this.onstop()
                }
            } else {
                if (this.state != 'pause') {
                    const insert_keyframe =
                        this.video_frame_cnt % this.key_frame_freq == 0
                    if (insert_keyframe)
                        window.logManager.log(
                            `[wbcRecorder] insert_keyframe - ${this.video_frame_cnt}`
                        )
                    this.videoEncoder.encode(value, {
                        keyFrame: insert_keyframe,
                    })
                    this.video_frame_cnt++

                    // ? See is there any drop frame happened by comparing num of frames recived per sec v.s target FPS
                    // ? Re-Encode the same frame to fill the spot of dropped frames.
                    const now = Date.now()
                    const fps = this.videoSetting.frameRate
                    const miss_draw = Math.round(
                        (now - this.record_start_time) / (1000 / fps) -
                            this.video_frame_cnt
                    )
                    let draw_times =
                        this.last_frame_time == null ? 1 : miss_draw + 1
                    draw_times = Math.min(draw_times, fps) // backfill up to one second worth of frames

                    for (let i = 1; i < draw_times; i++) {
                        this.videoEncoder.encode(value, {
                            keyFrame: false,
                        })
                        this.video_frame_cnt++
                        console.log('draw_times: ', draw_times, i)
                    }
                    this.last_frame_time = now
                }
                value.close()
                this.videoFrameReader.read().then(processVideoFrame)
            }
        }
        this.video_frame_cnt = 0
        this.record_start_time = Date.now()
        this.videoFrameReader.read().then(processVideoFrame)

        if (this.hasAudio) {
            let aud_track = this.mediaStream.getAudioTracks()[0]
            let aprc = new window.MediaStreamTrackProcessor(aud_track)
            this.audioFrameReader = aprc.readable.getReader()
            this.audioEcoderPaused = false

            if (this.audioEncoder.state == 'closed') {
                // encoder may closed by browser due to inactivity
                this.build_audio_encoder()
            }

            const processAudioFrame = ({ done, value }) => {
                if (this.state == 'inactive' || done) {
                    this.audioEcoderPaused = true
                    window.logManager.warn('[wbcRecorder] CLOSE audio encoder')
                    value.close()
                    if (this.videoEncoderPaused) {
                        this.onstop()
                        window.logManager.warn(
                            '[wbcRecorder] onstop callback from audio encoder'
                        )
                    }
                } else {
                    if (this.state != 'pause') {
                        this.audioEncoder.encode(value)
                        // console.log("audio_value", value)
                    }
                    value.close()
                    this.audioFrameReader.read().then(processAudioFrame)
                }
            }
            this.audioFrameReader.read().then(processAudioFrame)
        }
    }

    pause() {
        this.state = 'pause'
    }

    stop() {
        this.state = 'inactive'
        this.video_frame_cnt = 0
        window.logManager.log(
            '%c [wbcRecorder] stop',
            'color: rgb(200, 20, 0);'
        )
    }

    align(chunks) {
        // align audio chunks timeline to video
    }

    async mux(chunks) {
        if (this.muxer === undefined) this.muxer = await window.MUXERModule()

        const handle = this.muxer.create_muxer(
            {
                width: this.videoSetting.width,
                height: this.videoSetting.height,
                fps: this.videoSetting.frameRate,
                codec: this.codec,
            },
            () => {}
        )
        let find_dops = false
        let find_spspps = false
        let chcnk_cnt = { video: 0, audio: 0 }

        for (let mix_chunk of chunks) {
            const chunk = mix_chunk.chunk
            const timestamp =
                mix_chunk.type == 'audio_meta' ? -1 : mix_chunk.chunk.timestamp
            const p = this.muxer._malloc(chunk.byteLength)

            let tmp = new Uint8Array(chunk.byteLength)
            if (chunk instanceof ArrayBuffer) {
                tmp = new Uint8Array(chunk)
            } else {
                chunk.copyTo(tmp)
            }
            this.muxer.HEAPU8.set(tmp, p)

            switch (mix_chunk.type) {
                case 'video':
                    if (!find_spspps && this.video_descriptor !== undefined) {
                        const vd = this.muxer._malloc(
                            this.video_descriptor.byteLength
                        )
                        this.muxer.HEAPU8.set(this.video_descriptor, vd)
                        this.muxer.add_avc_track(
                            handle,
                            this.videoSetting.width,
                            this.videoSetting.height,
                            this.video_descriptor.byteLength
                        )
                        this.muxer._free(vd)
                        find_spspps = true
                    }

                    const pts = this.muxer.mux_nal(handle, p, chunk.byteLength)
                    chcnk_cnt.video++
                    window.logManager.log(
                        `[wbcRecorder] mux video chunk: ${chcnk_cnt.video}, ${chunk.byteLength}, ${pts}, ${timestamp}`
                    )
                    break
                case 'audio':
                    if (!find_dops) {
                        const ad = this.muxer._malloc(
                            this.audio_descriptor.byteLength
                        )
                        this.muxer.HEAPU8.set(this.audio_descriptor, ad)
                        this.muxer.add_opus_track(
                            handle,
                            this.audioSetting.channelCount,
                            this.audioSetting.sampleRate,
                            this.audioSetting.sampleSize,
                            ad,
                            this.audio_descriptor.byteLength
                        )
                        find_dops = true
                        this.muxer._free(ad)
                    }
                    const aud_pts = this.muxer.mux_opus(
                        handle,
                        p,
                        chunk.byteLength,
                        chunk.duration
                    )
                    chcnk_cnt.audio++
                    window.logManager.log(
                        `[wbcRecorder] mux audio chunk: ${chcnk_cnt.audio}, ${aud_pts}, ${timestamp}`
                    )
                    break
                default:
                    throw Error('')
            }
            this.muxer._free(p)
        }

        const mp4_buff = this.muxer.finalize_muxer(handle) // dump result + destory muxer
        window.logManager.log(
            `[wbcRecorder] muxer-${handle} final mp4_buff: ${mp4_buff.byteLength}`
        )
        const copy_result = new Uint8Array(mp4_buff)
        this.muxer.free_filedump(handle)
        return copy_result
    }
}
